import { useMemo } from 'react';
import { useKFlags } from '@/hooks/use-k-flags';
import { jobsEmploymentsOptions } from '@/constants/job-constants';
import { useWorkTypes } from './workTypesApi';

export interface WorkTypeOption {
  label: string;
  value: string | number;
}

export interface UseWorkTypeOptionsArgs {
  /**
   * Include disabled work types. Filters want them, pickers do not (CORE-5331).
   *
   * A filter has to offer every type an existing record might carry: turning a
   * type off hides it from new work, it does not rewrite the jobs already on
   * it, and without this those records cannot be filtered for at all.
   *
   * A picker is the opposite — offering a disabled type there would let someone
   * assign retired work, which is what "disabled" exists to prevent. Leave this
   * off for anything that writes a value.
   */
  includeDisabled?: boolean;
}

/**
 * Source for the job/requisition "work type" dropdown.
 * Flag OFF → legacy static employment-type options (string enum values).
 * Flag ON  → custom work types (numeric ids as values); enabled only unless
 *            `includeDisabled` is set.
 *
 * Consumers branch their save payload on `isWorkTypesEnabled`:
 * ON → send `ats_work_type_id` (the chosen value), OFF → keep `employment_type`.
 */
export const useWorkTypeOptions = ({
  includeDisabled = false,
}: UseWorkTypeOptionsArgs = {}): {
  options: WorkTypeOption[];
  isWorkTypesEnabled: boolean;
  isLoading: boolean;
} => {
  const { tCustomiseWorkTypes } = useKFlags();
  const isWorkTypesEnabled = Boolean(tCustomiseWorkTypes);
  const { data: workTypes = [], isLoading } = useWorkTypes({
    enabled: isWorkTypesEnabled,
  });

  const options = useMemo<WorkTypeOption[]>(() => {
    if (!isWorkTypesEnabled) return jobsEmploymentsOptions;
    return workTypes
      .filter((workType) => includeDisabled || workType.enabled)
      .map((workType) => ({ label: workType.name, value: workType.id }));
  }, [isWorkTypesEnabled, workTypes, includeDisabled]);

  return {
    options,
    isWorkTypesEnabled,
    isLoading: isWorkTypesEnabled && isLoading,
  };
};
